import React, { useState } from "react";
import theme from "../../../Styles/theme.scss";
import styles from "./styles.module.scss";
import { useParams, useNavigate } from "react-router-dom";
// API
import {
  postHubInstallStatus,
  postHubProperty,
} from "@intelligentlilli/api-layer";
// State
import { useDispatch, useSelector } from "react-redux";
import {
  setLoading,
  setRefreshOrganisationHubs,
} from "../../../State/slices/session-slice";
import { updateServiceUserProperties } from "../../../State/slices/serviceUsers-slice";
// Components
import CTAButton from "../../../Components/CTAButton";
import InstallationCard from "../../../Components/InstallationCard";
import InstallationGoBackButton from "../../../Components/InstallationGoBackButton";
import InstallationSplash from "../../../Components/InstallationSplash";
import { useViewport } from "../../../Components/ViewportProvider";
// services
import { mobileBreakpoint } from "../../../Services/config";
// hooks
import { useGetUserRole, useFade } from "../../../Services/Hooks";
// icons
import {
  ThumbsUpIcon,
  ThumbsDownIcon,
} from "../../../Styles/Icons/DesignSystem";
import NewStyleModal from "../../../Components/NewStyleModal";
import InstallFailedForm from "../../../Forms/InstallFailedForm";
import { useInstallTracking } from "../InstallWrapper";
import * as Sentry from "@sentry/react";
import { getLEDGuardKey, getLEDCacheKey } from "../../../Services/Constants";

const InstallFinish = ({ goBackLink }) => {
  const { id, devicestring } = useParams(); // Get the resident id from the url parameter
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // sizing
  const { width } = useViewport();
  const isDesktop = width > mobileBreakpoint;
  const user = useSelector((state) => state.user.authData);

  const { userIsJustAnInstaller } = useGetUserRole();

  const server = useSelector((state) => state.session.server);
  const sensorLocations = useSelector((state) => state.sensorLocations);
  const serviceUsers = useSelector((state) => state.serviceUsers);
  const indexOfSU = serviceUsers.findIndex(
    (serviceUser) => serviceUser.id.toString() === id
  );
  // Local state
  const [installNotes, setNotes] = useState();
  const [isInstallSuccess, setIsInstallSuccess] = useState(false);
  const [stage, setStage] = useState("start");

  const { trackInstall } = useInstallTracking();

  // Exit install modal
  const [isExitVisible, setShowExitInstallModalAnimation, showExitAnimation] =
    useFade(false, 150);
  const goToSensorsScreen = () => {
    navigate(`/install/sensors/${id}/${devicestring}`);
    trackInstall({ action: "back_to_sensor_connection", residentId: `${id}` });
  };

  const completeInstallation = () => {
    // if the user refreshses on this screen, it will clear the sensor locations
    // when that happens, this will result in the 'Uh oh' Screen where the 'Try Again' button will
    // navigate the user back to the sensors screen
    if (!sensorLocations) {
      setStage("end");
      setIsInstallSuccess(false);
      return;
    }

    dispatch(setLoading(true));

    const requests = [
      postHubProperty(
        server,
        id,
        "SensorLocations",
        JSON.stringify(sensorLocations),
        "web"
      ).then((res) => {
        console.log("1- SensorLocations Res.status:", res.status);
        if (!res.ok) {
          setIsInstallSuccess(false);
          throw new Error("Failed to post SensorLocations");
        }
      }),
      postHubInstallStatus(
        server,
        id,
        { status: "complete", installationNotes: installNotes || "" },
        "web"
      ).then((res) => {
        console.log("3- InstallationStatus Res.status:", res.status);
        if (!res.ok) throw new Error("Failed to post InstallationStatus");
        Sentry.withScope((scope) => {
          scope.setTag("Install-API", "Install Status Complete");
          Sentry.captureMessage(
            `WEB - InstallationStatus complete for resident ID: ${id}, userId: ${user.key}, at ${new Date().toISOString()}`
          );
        });
        trackInstall({
          action: "add_install_notes",
          residentId: `${id}`,
        });
      }),
    ];

    Promise.all(requests)
      .then(() => {
        setIsInstallSuccess(true);
        setStage("end");
        dispatch(setRefreshOrganisationHubs(true));
        if (devicestring) {
          localStorage.removeItem(getLEDGuardKey(devicestring));
          localStorage.removeItem(getLEDCacheKey(devicestring));
        }
        trackInstall({ action: "complete_install", residentId: `${id}` });
      })
      .catch((err) => {
        console.error("completeInstallation error:", err);
        setIsInstallSuccess(false);
        setStage("end");
      })
      .finally(() => dispatch(setLoading(false)));
  };

  const updateStatusAndNavigate = (indexOfServiceUser, navigationPath) => {
    dispatch(
      updateServiceUserProperties({
        index: indexOfServiceUser,
        key: "installationStatus",
        value: "complete",
      })
    );
    navigate(navigationPath);

    trackInstall({ action: "view_service_user_profile" });
  };

  return (
    <>
      <InstallationCard style={styles.card}>
        <div className={styles.inner_card}>
          {stage === "start" && (
            <>
              <div data-private className={styles.top}>
                <div className={styles.instructions}>
                  <h2 className={styles.main_title}>Any notes?</h2>
                  <p>
                    Almost there! Use the space below to add any notes about
                    this install.
                  </p>
                </div>
                <label>
                  Install notes
                  <textarea
                    value={installNotes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. resident stays at family home on Thursdays..."
                    maxLength={1300}
                    className={styles.text_area}
                  />
                </label>
              </div>
              <div className={styles.buttons}>
                <div>
                  <InstallationGoBackButton
                    textColor={theme.primary6}
                    onClick={() => navigate(-1)}
                    label={"Back to sensors"}
                  />
                </div>
                <div>
                  <CTAButton
                    style={{ width: "100%" }}
                    onClick={completeInstallation}
                  >
                    Complete
                  </CTAButton>
                </div>
              </div>
            </>
          )}
          {stage === "end" && isInstallSuccess && (
            <InstallationSplash
              icon={<ThumbsUpIcon colour={theme.expected2} />}
              headers={["Your Lilli kit is installed and fully active!"]}
              message={[
                "The sensors are online and now monitoring activity and safety. You should be able to test each sensor and see their data within a few seconds.",
                "It only takes a few days to learn the household's pattern of behaviour. Once that is understood, you will be able to see if any activity or behaviour is outside of the expected range.",
              ]}
              innerContentHalfSize={true}
              isDesktop={isDesktop}
              nextButtonLabel={
                userIsJustAnInstaller
                  ? "Start another install"
                  : "View Lilli data"
              }
              nextButtonOnClick={
                userIsJustAnInstaller
                  ? () => updateStatusAndNavigate(indexOfSU, "/install")
                  : () =>
                      updateStatusAndNavigate(
                        indexOfSU,
                        `/lilli-users/${id}?tab=event%20log&view=day`
                      )
              }
              doNotFlexContainer={true}
            />
          )}
          {stage === "end" && !isInstallSuccess && (
            <InstallationSplash
              icon={<ThumbsDownIcon colour={theme.risk3} />}
              headers={[
                !sensorLocations
                  ? "Missing sensor locations"
                  : "Install failed",
              ]}
              message={[
                !sensorLocations
                  ? "Go back a step and provide missing sensor locations."
                  : "Let's try that again, but if the problem continues, please contact +44 (0)758 800 8527 or email ",
              ]}
              email={"support@intelligentlilli.com"}
              nextButtonLabel={"Try again"}
              nextButtonOnClick={goToSensorsScreen}
              isDesktop={isDesktop}
              goBackOnClick={() => setShowExitInstallModalAnimation(true)}
              goBackButtonLabel={"Exit install"}
              doNotFlexContainer={true}
            />
          )}
        </div>
      </InstallationCard>

      {/* Exit install modal */}
      {isExitVisible && (
        <NewStyleModal
          hide={() => setShowExitInstallModalAnimation(false)}
          showCloseIcon={true}
          title=""
          useFade={true}
          showAnimation={showExitAnimation}
        >
          <InstallFailedForm
            onComplete={() => navigate(goBackLink)}
            server={server}
            hubId={id}
            devicestring={devicestring}
          />
        </NewStyleModal>
      )}
    </>
  );
};

export default InstallFinish;
